import React, { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { accountsSelector, stripeBasePlansSelector } from '../account/account-selectors';
import { getStripeBasePlans } from '../account/actions/get-stripe-base-plans';
import { loadAccounts } from '../account/actions/load-accounts';

import { UiButton } from '../ui/ui-button';
import { UiDatePicker } from '../ui/ui-date-picker';
import { UiDialog } from '../ui/ui-dialog';

import { getApplicationSourceLogsOnDate } from './api/admin-api';

import { csvExporter } from '../util/csv-exporter';
import { capitalize, formatDate } from '../util/formatters';

type InputProps = {
  onClose: () => void;
};

const mapStateToProps = (state: any) => ({
  accounts: accountsSelector(state),
  basePlans: stripeBasePlansSelector(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    loadAccounts: () => dispatch(loadAccounts()),
    getStripeBasePlans: () => dispatch(getStripeBasePlans()),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const FIRST_RUN_OF_ACCOUNT_SCORES = '2021-12-22';

export const ExportAccountScoresDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ onClose, actions, accounts, basePlans }: Props) => {
  const [date, setDate] = useState<Moment | null>(moment());
  const [error, setError] = useState<string>('');

  useEffect(() => {
    actions.getStripeBasePlans();
    actions.loadAccounts();
  });

  const getLogsForDate = async () => {
    if (!date) {
      return [];
    }
    const res = await getApplicationSourceLogsOnDate(date.format('YYYY-MM-DD'));
    const logs: AccountScoreGenerationLog[] = await res.json();
    return logs;
  };

  const handleExport = async () => {
    const logs = await getLogsForDate();
    if (!logs.length) {
      setError('No scores found for this date');
      return;
    }
    const { scoringVersion, ranAt, accountScores } = logs[0];
    const filename = `customer_success_scoring_${scoringVersion}_${moment(ranAt).format(
      'MM/DD/YY',
    )}.csv`;
    const accountColumns = [
      {
        label: 'AccountId',
        value: 'accountId',
      },
      {
        label: 'Account Name',
        value: 'accountName',
      },
      {
        label: 'Start Date',
        value: 'subscriptionDate',
      },
      {
        label: 'MRR',
        value: 'mrr',
      },
      {
        label: 'Total Score',
        value: 'totalScore',
      },
      {
        label: 'Previous Score',
        value: 'previousScore',
      },
      {
        label: 'Delta',
        value: 'delta',
      },
    ];
    const scoreColumns = Object.keys(accountScores[0])
      .filter((s) => !s.includes('Score') && s !== 'accountId')
      .map((value) => ({ label: capitalize(value), value }));
    const exportFields = accountColumns.concat(scoreColumns);
    const exportData = accountScores.map((as) => {
      const account = accounts.find((a) => a.accountId === as.accountId);
      if (!account) {
        return {};
      }
      const mrr = basePlans?.find((plan) => plan.id === account.baseMonthlyRateStripePlanId);
      return {
        ...as,
        ...account,
        mrr: mrr && mrr.amount !== 0 ? mrr.amount / 100 : null,
        subscriptionDate: formatDate(account.subscriptionDate),
        delta: as.previousScore ? as.totalScore - as.previousScore : as.totalScore,
      };
    });
    const result = csvExporter(filename, exportFields, exportData);
    if (!result) {
      setError('Error while exporting');
    }
  };

  const handleDateChange = (newDate: Moment | null) => {
    setError('');
    setDate(newDate);
  };

  return (
    <UiDialog title="Export Account Scores" onClose={onClose}>
      <UiDialog.Content>
        <UiDatePicker
          defaultDate={date}
          onChange={handleDateChange}
          variant="static"
          enableToolbar
          autoOk
          disableFuture
          minDate={FIRST_RUN_OF_ACCOUNT_SCORES}
        />
        <div className="u-color-red typo-bold u-textAlign-center">{error}</div>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton onClick={onClose} variant="dismiss">
          Close
        </UiButton>
        <UiButton onClick={handleExport}>Export</UiButton>
      </UiDialog.Actions>
    </UiDialog>
  );
});
